import React, { useState, useEffect } from "react";
import { prerelease } from "semver";

import "./Hero.css";
import { getOS, capitalizeFirstLetter, platforms } from "./utils";
import Icon from "./icons";
import { ScrollDown } from "./ScrollDownComponent";

const useModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const open = () => {
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
  };

  return { open, close, isOpen };
};

export const Hero = ({ release: releases }) => {
  const [os, setOs] = useState(null);

  useEffect(() => {
    setOs(getOS());
  }, []);

  const { isOpen, open, close } = useModal();

  const release =
    releases?.filter((r) => r.supported && !prerelease(r.version))?.[0] ?? {};
  const linuxUrl = release?.linuxAppUrl;

  // Handle modal open/close effects
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("modal-open");
      const handleEsc = (e) => {
        if (e.key === "Escape") close();
      };
      window.addEventListener("keydown", handleEsc);
      return () => {
        document.body.classList.remove("modal-open");
        window.removeEventListener("keydown", handleEsc);
      };
    }
  }, [isOpen, close]);

  return (
    <div className="overflow-x-hidden">
      <div className="container">
        <div className="row">
          <div className="col">
            <div className="hero mt-lg-5 mt-xl-5 mt-xxl-0 pt-xl-4 pt-xxl-0">
              <div className="hero-container">
                <div className="hero-content">
                  <h1 className="display-1" style={{ fontWeight: 500 }}>
                    OBLAC Drives
                  </h1>
                  <p className="lead ps-2">
                    Latest version:{" "}
                    <span className="fw-bold">{release.version}</span>
                  </p>
                  <p className="mb-5 ps-2">
                    Configure, tune, and commission Synapticon SOMANET Drives
                    with a simple setup, effective tuning, and full
                    commissioning support for accurate and reliable motor
                    control.
                  </p>
                  <div className="hero-actions">
                    <div className="hero-actions-primary">
                      {os ? (
                        <>
                          {os.toLowerCase() === "linux" ? (
                            <button
                              type="button"
                              className="btn btn-lg btn-primary rounded d-inline-flex align-items-center"
                              onClick={open}
                            >
                              Download for Linux
                            </button>
                          ) : (
                            <a
                              href={release?.[`${os}AppUrl`]}
                              title={`Download ${release?.[`${os}AppHandle`]}`}
                              className="btn btn-lg btn-primary rounded d-inline-flex align-items-center"
                            >
                              Download for {capitalizeFirstLetter(os)}
                            </a>
                          )}

                          <a
                            href="#latest-stable-release"
                            className="btn btn-link d-inline-flex align-items-center"
                          >
                            View the changelog
                          </a>
                        </>
                      ) : (
                        platforms.map((name) =>
                          name.toLowerCase() === "linux" ? (
                            <button
                              key={"primary" + name}
                              type="button"
                              className="btn btn-lg btn-secondary rounded d-inline-flex align-items-center"
                              onClick={open}
                            >
                              {Icon.download}&nbsp; Setup for Linux
                            </button>
                          ) : (
                            <a
                              key={"primary" + name}
                              href={release?.[`${name}AppUrl`]}
                              title={`Download ${release?.[`${name}AppHandle`]}`}
                              className="btn btn-lg btn-secondary rounded d-inline-flex align-items-center"
                            >
                              {Icon.download}
                              &nbsp; Setup for {capitalizeFirstLetter(name)}
                            </a>
                          ),
                        )
                      )}
                    </div>

                    <div className="hero-actions-secondary">
                      {!os ? (
                        <a
                          href="#latest-stable-release"
                          className="btn btn-link"
                        >
                          View the changelog
                        </a>
                      ) : null}
                      {platforms
                        .filter((name) => name !== os)
                        .map((name) =>
                          name.toLowerCase() === "linux" ? (
                            <button
                              key={name}
                              type="button"
                              className="btn btn-link me-2"
                              onClick={open}
                            >
                              Download for Linux
                            </button>
                          ) : (
                            <a
                              key={name}
                              href={release?.[`${name}AppUrl`]}
                              title={`Download ${release?.[`${name}AppHandle`]}`}
                              className="btn btn-link me-2"
                            >
                              Download for {capitalizeFirstLetter(name)}
                            </a>
                          ),
                        )}
                    </div>
                  </div>
                </div>

                <div className="hero-splash">
                  <img
                    sizes="(max-width: 1080px) 100vw, 1080px"
                    srcSet="
/images/splash/splash_270.png 270w,
/images/splash/splash_540.png 540w,
/images/splash/splash_1080.png 1080w,
/images/splash/splash_2160.png 2160w"
                    src="/images/splash/splash_2160.png"
                    alt=""
                  />
                </div>
              </div>

              <ScrollDown />

              {isOpen && (
                <>
                  <div
                    className="modal-backdrop fade show"
                    onClick={close}
                  ></div>
                  <dialog
                    className="modal show text-body"
                    style={{ display: "block" }}
                    open
                  >
                    <div className="modal-dialog modal-lg modal-dialog-scrollable">
                      <div className="modal-content">
                        <div className="modal-header bg-primary text-white">
                          <h5 className="modal-title">Download for Linux</h5>
                          <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Close"
                            onClick={close}
                          ></button>
                        </div>
                        <div className="modal-body">
                          <div className="d-flex flex-column gap-3">
                            <div>
                              <div className="alert alert-warning" role="alert">
                                When using Linux,{" "}
                                <strong>Ubuntu 22.04 or later</strong> is
                                required due to a specific OS library
                                dependency.
                              </div>

                              <p>
                                The app will be installed under{" "}
                                <code>/usr/lib/oblac-drives</code>. If any other
                                version is installed, it will be overwritten.
                              </p>

                              <p>
                                To install it, either open the setup file using
                                the app center, or run the following command in
                                a terminal:
                              </p>

                              <p>
                                <code>
                                  sudo apt install ./
                                  {linuxUrl?.split("/").pop()}
                                </code>
                              </p>
                            </div>
                            <div className="bg-white d-flex justify-content-end border-top pt-3">
                              <a
                                href={linuxUrl}
                                title={`Download ${release?.linuxAppHandle}`}
                                className="btn btn-primary"
                                download
                                onClick={close}
                              >
                                Download OBLAC Drives for Linux
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </dialog>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
